import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ProduitLot3AngesReveDoux() {
  const productId = 'lot-3-anges-reve-doux';
  const defaultImage = 'https://public.readdy.ai/ai/img_res/b9123dc7-dcd6-4b4d-a813-250efe08a010.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  const product = {
    name: 'Lot de 3 Anges "Joyeux coeur"',
    price: 10,
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/19d4a92b8550e0eaf575afccfa540ece.png',
    description: '🌙 Trois anges pour trois rêves tendres\nCes petits anges "Rêve Doux" veillent sur la maison avec délicatesse.\nLeur visage paisible et leur texture légère apportent un souffle poétique à votre décoration.\nIdéals à offrir ou à suspendre ensemble pour créer un nuage de douceur 💕',
    details: {
      fabrication: 'Fait main dans l\'atelier Les Carnets de Lolotte',
      type: 'Lot de 3 pièces artisanales identiques',
      dimensions: '8 cm (hors ficelle) chacun',
      delai: '2 à 3 jours',
      materials: 'Pages de livres anciens recyclés, ficelle naturelle',
      contenu: '3 anges identiques "Rêve Doux"'
    }
  };

  const handlePurchase = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/supabase/functions/create-payment-vitrine', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productName: product.name,
          amount: product.price * quantity * 100, // Stripe utilise les centimes
          quantity: quantity,
          currency: 'eur',
          description: `${product.name} - Quantité: ${quantity}`,
          metadata: {
            product_type: 'lot_3_anges_reve_doux',
            quantity: quantity.toString(),
            fabrication_delay: '2-3 jours'
          }
        }),
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />
      
      {/* Breadcrumb */}
      <section className="py-6 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <Link to="/" className="text-[#C49B86] hover:text-[#B97A72] transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line text-[#6B5E5A]"></i>
            <Link to="/noel" className="text-[#C49B86] hover:text-[#B97A72] transition-colors">Collection de Noël</Link>
            <i className="ri-arrow-right-s-line text-[#6B5E5A]"></i>
            <span className="text-[#6B5E5A]">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Fiche produit */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Image du produit */}
            <div>
              <div className="w-full h-[500px] bg-white rounded-lg overflow-hidden shadow-lg mb-4">
                <img
                  src={productImage}
                  alt="Lot de 3 Anges Rêve Doux"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Bouton pour modifier l'image */}
              <div className="text-center">
                <button
                  onClick={() => setShowImageUploader(!showImageUploader)}
                  className="bg-[#B97A72] text-white px-6 py-3 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-image-edit-line mr-2"></i>
                  {showImageUploader ? 'Fermer' : '📸 Modifier l\'image'}
                </button>
              </div>

              {/* Module de changement d'image */}
              {showImageUploader && (
                <div className="mt-6 bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Modifier l'image du produit
                  </h3>
                  <LocalImageUploader
                    productId={productId}
                    currentImage={productImage}
                    onImageUpdate={handleImageUpdate}
                  />
                </div>
              )}
            </div>

            {/* Informations produit */}
            <div className="space-y-8">
              
              {/* Titre et prix */}
              <div>
                <h1 className="text-4xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {product.name}
                </h1>
                <div className="flex items-center space-x-4 mb-6">
                  <span className="text-3xl font-bold text-[#C49B86]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.price}€
                  </span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Disponible
                  </span>
                  <span className="bg-[#F8EDEA] text-[#B97A72] px-3 py-1 rounded-full text-sm font-medium border border-[#C49B86]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Lot économique
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                {product.description.split('\n').map((line, index) => (
                  <p key={index} className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {line}
                  </p>
                ))}
              </div>

              {/* Détails techniques */}
              <div className="bg-[#FAF9F8] p-6 rounded-lg border border-[#C49B86]">
                <h3 className="text-xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Détails du produit
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Fabrication :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.fabrication}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Contenu :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.contenu}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Dimensions :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.dimensions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Délai :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.delai}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Matériaux :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.materials}</span>
                  </div>
                </div>
              </div>

              {/* Avantage du lot */}
              <div className="bg-[#EBD2CF] p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <i className="ri-gift-line text-[#B97A72] text-xl"></i>
                  <span className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Avantage du lot
                  </span>
                </div>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Économisez 4€ par rapport à l'achat de 3 anges individuels (3 × 4€ = 14€)
                </p>
              </div>

              {/* Sélection quantité et achat */}
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <label className="text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité :
                  </label>
                  <div className="flex items-center border-2 border-[#C49B86] rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-[#C49B86] hover:bg-[#F8EDEA] transition-colors"
                      disabled={quantity <= 1}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="px-4 py-2 text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-[#C49B86] hover:bg-[#F8EDEA] transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-lg text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Total : <span className="font-bold text-[#C49B86]">{(product.price * quantity).toFixed(2)}€</span>
                  </span>
                </div>

                {/* Bouton d'achat */}
                <button
                  onClick={handlePurchase}
                  disabled={isLoading}
                  className="w-full bg-[#F8EDEA] text-[#B97A72] px-8 py-4 rounded-full font-semibold text-lg border-2 border-[#C49B86] hover:bg-[#C49B86] hover:text-white transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Traitement en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-shopping-cart-line mr-2"></i>
                      Acheter maintenant
                    </>
                  )}
                </button>

                {/* Phrase poétique */}
                <div className="text-center pt-4">
                  <p className="text-lg text-[#C49B86] italic font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    💫 <em>Trois rêves, trois lumières, un seul cœur...</em>
                  </p>
                </div>
              </div>

              {/* Informations de livraison */}
              <div className="bg-[#EBD2CF] p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <i className="ri-truck-line text-[#B97A72] text-xl"></i>
                  <span className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Livraison soignée
                  </span>
                </div>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Vos 3 anges seront emballés avec soin et expédiés sous 2 à 3 jours ouvrés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Détails et Informations */}
      <section className="py-16 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Détails de fabrication
              </h2>
              <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p className="flex items-center">
                  <i className="ri-hand-heart-line text-[#C49B86] mr-3"></i>
                  Fait main dans l'atelier Les Carnets de Lolotte
                </p>
                <p className="flex items-center">
                  <i className="ri-book-open-line text-[#C49B86] mr-3"></i>
                  Créés à partir de pages de livres anciens pliées avec soin
                </p>
                <p className="flex items-center">
                  <i className="ri-ruler-line text-[#C49B86] mr-3"></i>
                  Hauteur : environ 8 cm chacun (hors ficelle)
                </p>
                <p className="flex items-center">
                  <i className="ri-time-line text-[#C49B86] mr-3"></i>
                  Délai de fabrication : 2 à 3 jours ouvrés
                </p>
                <p className="flex items-center">
                  <i className="ri-moon-line text-[#C49B86] mr-3"></i>
                  Lot de 3 pièces artisanales identiques "Rêve Doux"
                </p>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Idées d'utilisation
              </h2>
              <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p className="flex items-start">
                  <i className="ri-plant-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Suspendre aux branches du sapin de Noël</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-door-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Décorer une couronne de l'Avent</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-moon-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Placer dans une chambre pour veiller sur les rêves</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-home-heart-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Disposer sur une table de chevet ou une étagère</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-heart-3-line text-[#C49B86] mr-3 mt-1"></i>
                  <span>Créer un nuage de douceur et de sérénité</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
